import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Al cerrarse un diálogo, el navegador le devuelve el foco a lo que lo tenía antes de abrirlo (p. ej.
 * el buscador de Vender), pero a un campo de texto se lo devuelve sin el cursor: queda remarcado y lo
 * que se teclea no entra. Un focus() no lo arregla porque ya tiene el foco: hay que sacárselo y
 * devolvérselo. En pantallas táctiles no, para no abrir el teclado en pantalla.
 */
function restoreTextCursor() {
  const el = document.activeElement;
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) || matchMedia('(pointer: coarse)').matches) return;
  el.blur();
  el.focus({ preventScroll: true });
}

/** Cierra el diálogo y deja listo para escribir el campo de texto al que vuelve el foco. */
export function closeDialog(dialog: HTMLDialogElement): void {
  dialog.close();
  restoreTextCursor();
}

/** Diálogo modal nativo (<dialog>): accesible, cierra con Escape y no necesita librerías. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  dismissible = true,
  onKeyDown,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** false: no se cierra con Escape ni tocando afuera (hay que usar un botón). */
  dismissible?: boolean;
  /** Teclas apretadas en cualquier parte del diálogo. */
  onKeyDown?: (e: KeyboardEvent<HTMLDialogElement>) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // Si la app lo cierra (open pasa a false, p. ej. para abrir otro diálogo encima), el evento
  // "close" del navegador NO tiene que avisar como si lo hubiera cerrado la persona: eso
  // cerraba también lo que venía después (el botón Devolver "no hacía nada").
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // El autoFocus de React corre antes de abrir el diálogo, cuando todavía no se puede enfocar
      // nada, y el navegador enfoca el primer botón (p. ej. «WhatsApp» en vez de «Nueva venta»).
      // Por eso los componentes con autoFocus se marcan con data-autofocus y se enfocan acá.
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }
    if (!open && dialog.open) closeDialog(dialog);
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={() => {
        if (!openRef.current) return;
        // Lo cerró el navegador (Escape): el cursor del campo al que vuelve el foco se arregla acá.
        restoreTextCursor();
        onClose();
      }}
      onCancel={(e) => !dismissible && e.preventDefault()}
      // Enter mantenido apretado no confirma cosas de corrido (p. ej. cobrar y cerrar el vuelto).
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.repeat && !(e.target instanceof HTMLTextAreaElement)) e.preventDefault();
        onKeyDown?.(e);
      }}
      onClick={(e) => dismissible && e.target === ref.current && onClose()}
      className="m-auto w-[calc(100%-2rem)] max-w-lg rounded-2xl bg-white p-0 text-slate-900 shadow-raised ring-1 ring-slate-200 backdrop:bg-black/50"
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col overflow-hidden rounded-2xl">
          <div className="border-b border-slate-100 px-5 py-3.5">
            <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          </div>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}
